"use client";

import { useState } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export type LoaderSet = "A" | "B" | "C";

// Predefined sets of 3 GIFs each
const loaderSets = {
  A: [
    "/Gardening_GIF/Wheelbarrow.gif",
    "/Nature_GIF/Flower_02.gif",
    "/Gardening_GIF/Sprey.gif",
  ],
  B: [
    "/Nature_GIF/Plant_01.gif",
    "/Nature_GIF/Plant_02.gif",
    "/Nature_GIF/Plant_03.gif",
  ],
  C: [
    "/Nature_GIF/Tree_01.gif",
    "/Nature_GIF/Tree_02.gif",
    "/Nature_GIF/Tree_03.gif",
  ],
} as const;

// Fallback CSS spinner icons
const FallbackIcon = ({ index, size }: { index: number; size: number }) => {
  const icons = [
    // Plant icon
    <svg
      key="plant"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="text-emerald-600"
    >
      <path
        d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 100-16 8 8 0 000 16zm-1-5h2v2h-2v-2zm0-2v-2a2 2 0 012-2h1a1 1 0 000-2h-1a4 4 0 00-4 4v2h2z"
        fill="currentColor"
      />
    </svg>,
    // Seed icon
    <svg
      key="seed"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="text-emerald-600"
    >
      <circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.2" />
      <circle cx="12" cy="12" r="4" fill="currentColor" />
    </svg>,
    // Tree icon
    <svg
      key="tree"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="text-emerald-600"
    >
      <path d="M12 2L4 8v6l8 6 8-6V8l-8-6z" fill="currentColor" opacity="0.2" />
      <path d="M12 2L4 8l8 6 8-6-8-6z" fill="currentColor" />
    </svg>,
  ];
  return icons[index % icons.length];
};

interface NatureLoaderProps {
  set?: LoaderSet;
  className?: string;
  imageSize?: number;
}

export function NatureLoader({
  set = "A",
  className,
  imageSize = 48,
}: NatureLoaderProps) {
  const images = loaderSets[set];
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());

  const handleImageError = (index: number) => {
    setImageErrors((prev) => new Set(prev).add(index));
  };

  return (
    <div className={cn("flex items-center gap-4", className)}>
      {images.map((src, index) => (
        <div
          key={src}
          className="relative animate-fade-in"
          style={{
            width: imageSize,
            height: imageSize,
            animationDelay: `${index * 0.1}s`,
          }}
        >
          {imageErrors.has(index) ? (
            // Fallback to CSS animated icon
            <div className="w-full h-full flex items-center justify-center animate-pulse">
              <FallbackIcon index={index} size={imageSize * 0.7} />
            </div>
          ) : (
            <Image
              src={src}
              alt={`Loading animation ${index + 1}`}
              fill
              className="object-contain"
              onError={() => handleImageError(index)}
            />
          )}
        </div>
      ))}
    </div>
  );
}
